import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImagePlus } from "lucide-react";
import { BRACKET } from "../data/products";
import { REVIEWS, REVIEW_SUMMARY, reviewKey } from "../data/reviews";
import { reviewCountLabel } from "../components/RatingBadge";
import ReviewPhoto from "../components/ReviewPhoto";
import { BRACKET_IMAGES } from "../data/productImages";
import { RANGE_CARDS, HOME_RANGE_CARDS } from "../data/range";
import { DISPATCH_WINDOW } from "../data/hardware";
import { PREMIUM_TIERS, FLAT_TIERS, bracketPricing, formatAUD } from "../lib/pricing";
import Seo, { breadcrumbJsonLd, faqPageJsonLd } from "../components/Seo";
import { Eyebrow, Stars, PremiumVideo } from "../components/PremiumBits";
import useMediaQuery from "../hooks/useMediaQuery";

// Rotating full-bleed job photos behind the hero. First frame holds ~16s, the
// rest ~7s (see interval below). Paused under reduced motion / hidden tab.
const HERO_SLIDES = [
  { src: "/images/hero-slide-subframe.jpg", alt: "A cladding subframe part-way through install, with black StakeTek brackets on the Colorbond boundary carrying treated pine rails" },
  { src: "/images/hero-slide-rural.jpg", alt: "A rural boundary fence on galvanised tube posts with black 27 degree animal extensions carrying barbed wire" },
];

// The wide job photos all cover-crop to a meaningless slice in a portrait hero,
// so phones show a single static shot (the subframe install) and skip the
// carousel entirely.
const HERO_SLIDES_MOBILE = [HERO_SLIDES[0]];

// Prices below are read from the live tier tables, never typed in.
const SUPPORTED_BASE = PREMIUM_TIERS[0];
const SUPPORTED_BEST = PREMIUM_TIERS[PREMIUM_TIERS.length - 1];

// The flagship bracket, featured in the hero. It opens the configurator, so the
// action reads "Choose options".
const FEATURED = {
  to: "/products/supported-bracket",
  spec: "2.5 mm Duragal angle",
  name: "Supported slide-on bracket",
  img: "/images/staketek-supported-bracket-studio-gal-angled.jpg",
  alt: "StakeTek supported slide-on bracket in galvanised steel",
};

// Four real jobs, each saying what the bracket is doing in the photo and
// linking to the guide or build that covers it. No load claims.
const BUILDS = [
  {
    title: "Concrete formwork",
    img: "/images/gallery-03.jpg",
    pos: "50% 60%",
    alt: "A slide-on bracket on a driven star picket holding timber boxing at the corner of a freshly poured slab",
    caption: "Flat brackets hold the boxing to driven pickets, then come off again after the pour.",
    to: "/blog/star-picket-formwork-and-concrete-boxing",
    label: "Formwork guide",
  },
  {
    title: "Timber fencing",
    img: "/images/real-bracket-corner-frame.jpg",
    pos: "50% 45%",
    alt: "StakeTek brackets fixing timber top and mid rails to star pickets at a fence corner",
    caption: "Brackets fix the top and mid rails to the star pickets at a fence corner.",
    to: "/applications/timber-rails-on-star-pickets",
    label: "Timber rails build",
  },
  {
    title: "Screens & cladding",
    img: "/images/staketek-supported-bracket-cladding-subframe.jpg",
    pos: "60% 40%",
    alt: "Galvanised StakeTek brackets fixing treated pine rails to black star pickets, with black slat cladding fixed over the finished frame",
    caption: "Brackets fix treated pine rails to the pickets. The slat screen fixes to the rails.",
    to: "/applications/privacy-screen-and-windbreak",
    label: "Privacy screen build",
  },
  {
    title: "Garden builds",
    img: "/images/real-bracket-bench-build.jpg",
    pos: "50% 55%",
    alt: "A timber bench on driven star pickets, with the seat and backrest boards fixed to StakeTek brackets",
    caption: "A bench on driven pickets, with the seat and backrest fixed to brackets.",
    to: "/blog/garden-beds-and-timber-frames-on-star-pickets",
    label: "Garden frames guide",
  },
];

// Short uses row under the build photos. Every one of these is either already
// listed as an application in data/products.js or shown in a build guide.
// Deck and platform sub-frames were taken off on 2026-09-18 on Tanner's call:
// the site doesn't pitch the brackets for super heavy jobs like decks.
const ALSO_USED_FOR = [
  "Temporary fencing",
  "Site setups and hoarding",
  "Shade and screen frames",
  "Stock and animal yards",
  "Tree guards",
  "Raised garden beds",
  "Shelving and benches",
];

// Quantity bands for the bulk table, built from the tier tables so the rows,
// the band edges and every price follow lib/pricing.js.
const BULK_ROWS = PREMIUM_TIERS.map((t, i) => {
  const next = PREMIUM_TIERS[i + 1];
  return {
    band: next ? `${t.min}–${next.min - 1}` : `${t.min}+`,
    supported: formatAUD(bracketPricing(t.min, PREMIUM_TIERS).unitNet),
    flat: formatAUD(bracketPricing(t.min, FLAT_TIERS).unitNet),
    best: !next,
  };
});

// Homepage review cards start with this many; the rest open in place.
const REVIEWS_SHOWN = 3;

// The orange section's three points. Welding is the product's strength; the
// only "no welding" is on site.
const BENEFITS = [
  { title: "Welded construction", body: "The sleeve and the angle are two separate pieces of steel, welded together." },
  { title: "Slides straight on", body: "Fits standard Y-profile star pickets. No welding or fabrication on site." },
  { title: "Lifetime defect warranty", body: "Covers manufacturing defects under normal use.", to: "/returns-warranty#warranty", label: "What's covered" },
  { title: "Invented and made here", body: "The bracket is our family's own invention, made in Victoria." },
];

// Why it suits a DIY job. Every line is something the product data or the
// install video actually backs up: no site welding, hand tools, adjustable and
// reusable, and our own design made in Victoria.
const DIY = [
  { title: "Nothing to dig or pour", body: "The picket does the ground work, so there are no post holes and no concrete to wait on." },
  { title: "Tools you already have", body: "Drive your pickets in the way you always do, then it is a drill and a level for the brackets." },
  { title: "A season or for good", body: "Galvanised and built for long-term outdoor use, but nothing is set in concrete. Leave it out there, or back the fixings out and use it on the next job." },
  { title: "An Australian innovation", body: "Invented by our family and made in Victoria." },
];

// Learn more: one card per guide. `pair` shows the two brackets side by side.
const GUIDES = [
  {
    to: "/blog/choosing-the-right-star-picket-bracket",
    title: "Choosing the right bracket",
    body: "Supported or flat, what the steel thicknesses are, and which one suits rails, sheet or formwork.",
    pair: [
      { src: "/images/staketek-supported-bracket-studio-gal-angled.jpg", alt: "Supported slide-on bracket" },
      { src: "/images/staketek-flat-bracket-white-1.jpg", alt: "Flat slide-on bracket" },
    ],
  },
  {
    to: "/blog/how-to-install-staketek-brackets",
    title: "How to install StakeTek brackets",
    body: "Fitting, setting the height, the M8 bolt and fixing the timber, with the installation video.",
    img: "/videos/staketek-installation-demo-poster.jpg",
    pos: "50% 62%",
    alt: "A bracket being fitted to a driven star picket beside a timber boxing frame",
    badge: "Includes video",
  },
  {
    to: "/blog/fence-extensions-explained",
    title: "Fence extensions explained",
    body: "Galvanised or aluminium, joining a second picket, the angled animal extensions and what fits what.",
    img: "/images/staketek-fence-extension-detail.jpg",
    pos: "50% 30%",
    alt: "A galvanised fence extension sleeve over a star picket, with a second picket joined into the top",
  },
];

// Keeps hyphenated words ("Slide-On") on one line, so a narrow card never
// breaks a product name into "Slide- / On".
const keepHyphens = (text) =>
  text.split(/(\S+-\S+)/).map((part, i) =>
    part.includes("-") ? <span key={i} style={{ whiteSpace: "nowrap" }}>{part}</span> : part
  );

// "Verified purchase · via Loox", "Facebook", etc. Each part only when held.
const reviewSource = (r) => [r.verified ? "Verified purchase" : null, r.source].filter(Boolean).join(" · ");
const reviewProduct = (r) => (r.product ? (r.variant ? `${r.product}, ${r.variant}` : r.product) : null);

export default function Home() {
  const [slide, setSlide] = useState(0);
  const [showAllReviews, setShowAllReviews] = useState(false);
  // Phones get the single static slide. Both lists start at the same photo, so
  // switching between them never shows a different first frame.
  const isMobile = useMediaQuery("(max-width: 640px)");
  const heroSlides = isMobile ? HERO_SLIDES_MOBILE : HERO_SLIDES;

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let i = 0;
    let timer;
    const tick = () => {
      timer = window.setTimeout(() => {
        if (!document.hidden) {
          i = (i + 1) % HERO_SLIDES.length;
          setSlide(i);
        }
        tick();
      }, i === 0 ? 16000 : 7000);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, []);

  const reviewCount = REVIEW_SUMMARY?.count ?? 0;
  const reviewAvg = REVIEW_SUMMARY?.average ?? 0;
  const visibleReviews = showAllReviews ? REVIEWS : REVIEWS.slice(0, REVIEWS_SHOWN);
  const hiddenReviews = REVIEWS.length - REVIEWS_SHOWN;
  const heroPrev = () => setSlide((s) => (s + heroSlides.length - 1) % heroSlides.length);
  const heroNext = () => setSlide((s) => (s + 1) % heroSlides.length);

  return (
    <>
      <Seo
        title="Star Picket & Fence Post Brackets | StakeTek Australia"
        description="Australian-made galvanised brackets that slide onto star pickets and fence posts — no welding. Fix timber, Colorbond, rails and panels fast. Fence brackets, flat brackets, posts and kits, shipped Australia-wide."
        path="/"
        keywords="star picket brackets, fence post brackets, post brackets, fence brackets, fence post bracket, colorbond fence brackets, galvanised fence brackets, building brackets, timber to steel bracket, no weld fence brackets, picket brackets, premium fence hardware, StakeTek, fence brackets Australia"
        image={BRACKET_IMAGES.websitePrimary}
        jsonLd={[
          breadcrumbJsonLd([{ name: "Home", path: "/" }]),
          faqPageJsonLd(BRACKET.faqs),
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            url: "https://staketek.com.au/",
            name: "StakeTek",
            slogan: "Building Better Outdoors",
            description:
              "Australian family-owned maker of outdoor building hardware and systems, based in Victoria, where its slide-on brackets are designed and manufactured. StakeTek's current range is built around heavy-duty galvanised slide-on star picket brackets, alongside flat brackets, tube-type fence posts, extensions and matched installation kits.",
            founder: { "@type": "Person", name: "Tanner Frazzetto" },
            telephone: "+61432068695",
            email: "sales@staketek.com.au",
            address: { "@type": "PostalAddress", addressRegion: "VIC", addressCountry: "AU" },
            areaServed: "AU",
            // Topical expertise signals — help Google associate the StakeTek
            // brand with the wider set of fencing terms, not just star pickets.
            knowsAbout: [
              "Star picket brackets",
              "Fence post brackets",
              "Fence brackets",
              "Colorbond fencing hardware",
              "Galvanised steel fencing hardware",
              "Rural and boundary fencing",
              "Tube-type fence posts",
              "Concrete-mounted star picket bases",
              "Fence height extensions",
            ],
            sameAs: ["https://www.facebook.com/StakeTek/", "https://www.instagram.com/staketek/"],
          },
        ]}
      />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="pd-hero" data-testid="home-hero">
        {heroSlides.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            className="pd-hero-img"
            style={{ opacity: slide % heroSlides.length === i ? 1 : 0 }}
            /* Both slides load eagerly: the second sits at opacity 0 in the
               viewport, and lazy-loading never fetched it, so the hero went
               blank when the carousel rotated to it. Priority still favours
               the first frame. */
            loading="eager"
            fetchPriority={i === 0 ? "high" : "low"}
          />
        ))}
        <div aria-hidden="true" className="pd-hero-scrim-a" />
        <div aria-hidden="true" className="pd-hero-scrim-b" />

        <div className="pd-hero-grid">
          {heroSlides.length > 1 && (
            <div className="pd-hero-arrows">
              <button type="button" onClick={heroPrev} aria-label="Previous project photo" className="pd-hero-arrow">‹</button>
              <button type="button" onClick={heroNext} aria-label="Next project photo" className="pd-hero-arrow">›</button>
            </div>
          )}

          <div>
            <h1 data-testid="home-hero-title">
              <span className="pd-hero-line">Star picket brackets</span>{" "}
              <span className="pd-hero-line">&amp; fence extensions.</span>{" "}
              <span className="pd-hero-line pd-hero-line--accent">Engineered for real jobs.</span>
            </h1>

            <div className="pd-hero-bottom">
              <div className="pd-hero-sub">
                <p>Heavy-duty welded brackets that slide directly onto standard Y-profile star pickets. No welding or fabrication on site.</p>
                <div className="pd-hero-ctas">
                  <Link to="/products/brackets" className="pd-btn pd-btn--orange" data-testid="hero-cta-shop-brackets">Shop brackets</Link>
                  <Link to="/products/fence-height-extensions" className="pd-btn pd-btn--ghost" data-testid="hero-cta-extensions">Fence extensions</Link>
                </div>
              </div>

              <div className="pd-hero-feature">
                <Link to={FEATURED.to} className="pd-hero-card" data-testid="hero-featured-bracket">
                  <span className="pd-hero-card-media">
                    <img src={FEATURED.img} alt={FEATURED.alt} width="600" height="600" />
                  </span>
                  <span className="pd-hero-card-body">
                    <span className="pd-hero-card-spec">{FEATURED.spec}</span>
                    <span className="pd-hero-card-name">{keepHyphens(FEATURED.name)}</span>
                    <span className="pd-hero-card-price">
                      <strong>{formatAUD(SUPPORTED_BASE.unit)}</strong> each
                      <span className="pd-hero-card-best">{formatAUD(SUPPORTED_BEST.unit)} each at {SUPPORTED_BEST.min}+</span>
                    </span>
                    <span className="pd-hero-cardcta">Choose options <span aria-hidden="true">→</span></span>
                  </span>
                </Link>
                <div className="pd-hero-proof">
                  {reviewCount > 0 ? (
                    <Link to="/reviews">
                      <span aria-hidden="true" className="pd-hero-proof-stars">★★★★★</span>
                      {reviewAvg.toFixed(1)} · {reviewCount} reviews
                    </Link>
                  ) : (
                    <Link to="/reviews">Reviews</Link>
                  )}
                  <span>Dispatch in {DISPATCH_WINDOW}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SHOP THE RANGE ───────────────────────────────────────────────── */}
      <section className="pd-section pd-section--parchment" data-testid="home-range">
        <div className="pd-wrap">
          <div className="pd-sechead">
            <div>
              <Eyebrow>The range</Eyebrow>
              <h2 className="pd-display pd-h2">Shop the range.</h2>
            </div>
            <Link to="/products" className="pd-tlink">All products <span aria-hidden="true">→</span></Link>
          </div>

          <RangeGrid cards={HOME_RANGE_CARDS} compact />

        </div>
      </section>

      {/* NOTE (2026-09-11 refresh): the orange quick-buy strip, the black weld
          band, "Customer job", "How it fits" and "Old method vs StakeTek" were
          removed to shorten the page. The quick-buy links repeated the range
          cards, the cladding job now leads the build photos below, the fitting
          steps live in the install guide and video, and the weld message moved
          into the orange section. All of it is in git history if wanted back. */}

      {/* ── INSTALL VIDEO ────────────────────────────────────────────────── */}
      <section className="pd-section pd-section--white pd-rule-top" data-testid="home-video">
        <div className="pd-wrap pd-videogrid">
          <div className="pd-videogrid-intro">
            <Eyebrow>Watch it fitted</Eyebrow>
            <h2 className="pd-display pd-h2">One bolt, two screws.</h2>
            <p className="pd-text">
              Slide the bracket over the picket, secure it with the M8 bolt and nut, then screw the timber on.
              This clip shows a timber boxing frame going up with a drill driver and a level.
            </p>
          </div>
          <div className="pd-videogrid-media">
            <PremiumVideo testid="home-install-video" />
          </div>
          <div className="pd-videogrid-cta">
            <Link to="/products/brackets" className="pd-btn pd-btn--orange" data-testid="home-video-cta">Shop brackets</Link>
          </div>
        </div>
      </section>

      {/* ── MADE FOR DIY ─────────────────────────────────────────────────── */}
      <section className="pd-section pd-section--black pd-diy" data-testid="home-diy">
        <div className="pd-wrap">
          <div className="pd-diy-head">
            <div>
              <Eyebrow tone="orange">Made for DIY</Eyebrow>
              <h2 className="pd-display pd-h2">If you can drive a picket, you can build it.</h2>
            </div>
            <p className="pd-diy-lead">
              No welding, no post holes and no concrete. Drive the picket, slide the bracket on and screw your
              timber to it. Leave it there for good, or change your mind later and it unbolts for the next job.
            </p>
          </div>
          <ul className="pd-diy-grid">
            {DIY.map((d) => (
              <li key={d.title}>
                <span aria-hidden="true" className="pd-diy-rule" />
                <h3>{d.title}</h3>
                <p>{d.body}</p>
              </li>
            ))}
          </ul>
          <div className="pd-diy-foot">
            <Link to="/blog/how-to-install-staketek-brackets" className="pd-tlink pd-tlink--ondark" data-testid="home-diy-guide">
              Read the install guide <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── APPLICATIONS ─────────────────────────────────────────────────── */}
      <section className="pd-section pd-section--parchment" data-testid="home-builds">
        <div className="pd-wrap">
          <div className="pd-sechead">
            <div>
              <Eyebrow>Built for more than fencing</Eyebrow>
              <h2 className="pd-display pd-h2">Wherever steel meets timber on site.</h2>
            </div>
            <Link to="/applications" className="pd-tlink" data-testid="home-applications-cta">View more build ideas <span aria-hidden="true">→</span></Link>
          </div>
          <div className="pd-builds">
            {BUILDS.map((b) => (
              <Link key={b.title} to={b.to} className="pd-build-card">
                <span className="pd-build-card-media pd-zoom">
                  <img src={b.img} alt={b.alt} style={{ objectPosition: b.pos }} loading="lazy" decoding="async" />
                </span>
                <span className="pd-build-card-body">
                  <span className="pd-build-card-title">{b.title}</span>
                  <span className="pd-build-card-caption">{b.caption}</span>
                  <span className="pd-card-cta">{b.label} <span aria-hidden="true">→</span></span>
                </span>
              </Link>
            ))}
          </div>
          <div className="pd-uses">
            <span className="pd-uses-label">Also built with it</span>
            <ul>
              {ALSO_USED_FOR.map((u) => <li key={u}>{u}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* ── BULK PRICING ─────────────────────────────────────────────────── */}
      <section className="pd-section pd-parallax-band" data-testid="home-bulk">
        <img src="/images/fence-paddock.jpg" alt="" loading="lazy" decoding="async" />
        <div aria-hidden="true" className="pd-parallax-scrim" />
        <div className="pd-wrap pd-bulk-grid">
          <div>
            <Eyebrow tone="orange">Bulk pricing</Eyebrow>
            <h2 className="pd-display pd-h2" style={{ color: "#fff" }}>Buy more, pay less.</h2>
            <p className="pd-bulk-lead">
              The price per bracket drops automatically as the quantity goes up. No code or account needed.
            </p>
            <div className="pd-btnrow">
              <Link to="/products/bulk-packs" className="pd-btn pd-btn--orange" data-testid="home-bulk-cta">Shop bulk packs</Link>
              <Link to="/trade" className="pd-btn pd-btn--ghost" data-testid="home-bulk-enquire">Enquire about larger orders</Link>
            </div>
          </div>
          <div className="pd-bulk-table">
            <table>
              <caption>Price per bracket, GST inclusive</caption>
              <thead>
                <tr>
                  <th scope="col">Quantity</th>
                  <th scope="col">Supported</th>
                  <th scope="col">Flat</th>
                </tr>
              </thead>
              <tbody>
                {BULK_ROWS.map((r) => (
                  <tr key={r.band} className={r.best ? "is-best" : undefined}>
                    <th scope="row">{r.band}</th>
                    <td>{r.supported}</td>
                    <td>{r.flat}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>Applied automatically at checkout.</p>
          </div>
        </div>
      </section>

      {/* ── REVIEWS ──────────────────────────────────────────────────────── */}
      {reviewCount > 0 && (
        <section className="pd-section pd-section--white" data-testid="home-reviews">
          <div className="pd-wrap">
            <div className="pd-sechead">
              <div>
                <Eyebrow>Reviews</Eyebrow>
                <h2 className="pd-display pd-h2">
                  What customers say.
                </h2>
              </div>
              <div className="pd-review-summary">
                <Stars size={17} tracking={3} count={Math.round(reviewAvg)} />
                <Link to="/reviews">{reviewAvg.toFixed(1)} · {reviewCountLabel(REVIEW_SUMMARY)} →</Link>
              </div>
            </div>
            <ul className="pd-reviews" id="home-review-list">
              {visibleReviews.map((r) => (
                <li key={reviewKey(r)} className="pd-review-card">
                  {Number(r.rating) > 0 && <Stars count={Math.round(Number(r.rating))} size={13} />}
                  {r.text ? (
                    <p className="pd-review-text">{r.text}</p>
                  ) : (
                    <p className="pd-review-text is-empty">Rated {r.rating} out of 5. No written comment.</p>
                  )}
                  <ReviewPhoto photo={r.photo} />
                  <div className="pd-review-by">
                    <span className="pd-review-name">{r.name}</span>
                    {reviewSource(r) && <span className="pd-review-meta">{reviewSource(r)}</span>}
                    {reviewProduct(r) && <span className="pd-review-meta">{reviewProduct(r)}</span>}
                  </div>
                </li>
              ))}
            </ul>
            <div className="pd-reviews-foot">
              {hiddenReviews > 0 && (
                <button
                  type="button"
                  className="pd-btn pd-btn--outline pd-btn--sm"
                  aria-expanded={showAllReviews}
                  aria-controls="home-review-list"
                  onClick={() => setShowAllReviews((v) => !v)}
                  data-testid="home-reviews-toggle"
                >
                  {showAllReviews ? "Show fewer reviews" : `Show ${hiddenReviews} more review${hiddenReviews === 1 ? "" : "s"}`}
                </button>
              )}
              <Link to="/reviews" className="pd-tlink">Read all reviews <span aria-hidden="true">→</span></Link>
            </div>
          </div>
        </section>
      )}

      {/* ── LEARN MORE ───────────────────────────────────────────────────── */}
      <section className="pd-section pd-section--parchment pd-rule-top" data-testid="home-guides">
        <div className="pd-wrap">
          <div className="pd-sechead">
            <div>
              <Eyebrow>Learn more</Eyebrow>
              <h2 className="pd-display pd-h2">Pick it. Fit it. Extend it.</h2>
            </div>
            <Link to="/blog" className="pd-tlink">All guides <span aria-hidden="true">→</span></Link>
          </div>
          <div className="pd-guides">
            {GUIDES.map((g) => (
              <Link key={g.to} to={g.to} className="pd-guide-card" data-testid={`home-guide-${g.to.split("/").pop()}`}>
                <span className={`pd-guide-card-media${g.pair ? " is-pair" : ""}`}>
                  {g.pair ? (
                    g.pair.map((p) => <img key={p.src} src={p.src} alt={p.alt} loading="lazy" decoding="async" />)
                  ) : (
                    <img src={g.img} alt={g.alt} style={{ objectPosition: g.pos }} loading="lazy" decoding="async" />
                  )}
                  {g.badge && <span className="pd-guide-card-badge">{g.badge}</span>}
                </span>
                <span className="pd-guide-card-body">
                  <span className="pd-guide-card-title">{g.title}</span>
                  <span className="pd-guide-card-text">{g.body}</span>
                  <span className="pd-card-cta">Read the guide <span aria-hidden="true">→</span></span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSER (orange) ──────────────────────────────────────────────── */}
      <section className="pd-section pd-closer" data-testid="home-closer">
        <div aria-hidden="true" className="pd-closer-lines" />
        <div className="pd-wrap pd-closer-grid">
          <div>
            <div className="pd-closer-eyebrow">
              <span aria-hidden="true" />
              The StakeTek bracket
            </div>
            <h2 className="pd-display pd-closer-title">This is how<br />you build on<br />star pickets.</h2>
            <p className="pd-closer-lead">Available in supported and flat configurations for real fencing, cladding and construction jobs. Simple enough for a weekend project, heavy enough for the trade.</p>
          </div>
          <div className="pd-closer-actions">
            <Link to="/products/brackets" className="pd-btn pd-btn--dark" data-testid="home-closer-cta">Shop brackets <span aria-hidden="true">→</span></Link>
            <p className="pd-closer-note">
              {formatAUD(SUPPORTED_BASE.unit)} each, down to {formatAUD(SUPPORTED_BEST.unit)} from {SUPPORTED_BEST.min} up.
              Dispatched from Victoria in {DISPATCH_WINDOW}.
            </p>
            <div className="pd-closer-links">
              <Link to="/trade">Trade &amp; wholesale <span aria-hidden="true">→</span></Link>
              <Link to="/contact">Ask us a question <span aria-hidden="true">→</span></Link>
            </div>
          </div>
        </div>
        <ul className="pd-wrap pd-closer-proof">
          {BENEFITS.map((b) => (
            <li key={b.title}>
              <span aria-hidden="true" className="pd-closer-proof-rule" />
              <h3>{b.title}</h3>
              <p>{b.body}</p>
              {b.to && <Link to={b.to}>{b.label} <span aria-hidden="true">→</span></Link>}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

// Shared range grid (Home + Brackets hub). `compact` gives the homepage its
// two-up phone layout so the whole range fits in about a screen and a half.
export function RangeGrid({ cards = RANGE_CARDS, compact = false }) {
  return (
    <div className={`pd-range-grid${compact ? " pd-range-grid--compact" : ""}`}>
      {cards.map((c) => (
        <Link key={c.to + c.code} to={c.to} className="pd-card pd-rangecard" data-testid={`range-card-${c.code}`}>
          <span className="pd-rangecard-media pd-zoom" style={{ background: c.bg }}>
            {c.img ? (
              <img src={c.img} alt={c.name} style={{ objectFit: c.fit }} loading="lazy" decoding="async" />
            ) : (
              // No product photo yet — an honest fill slot, not a misleading render.
              <span className="pd-rangecard-empty">
                <ImagePlus size={26} strokeWidth={1.6} aria-hidden="true" />
                <span>Coming soon</span>
              </span>
            )}
          </span>
          <span className="pd-rangecard-body">
            <span className="pd-rangecard-tag">{c.tag}</span>
            <span className="pd-rangecard-name">{keepHyphens(c.name)}</span>
            <span className="pd-rangecard-note">{c.note}</span>
          </span>
          <span className="pd-rangecard-foot">
            <span className="pd-rangecard-price">
              <strong>{c.price}</strong>
              <span>{c.unit}</span>
            </span>
            <span className="pd-card-cta">{c.cta}<span aria-hidden="true"> →</span></span>
          </span>
        </Link>
      ))}
    </div>
  );
}
